import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';

export interface UseVoiceInputOptions {
	onTranscription: (text: string) => void;
	language?: string;
}

export interface UseVoiceInputReturn {
	isRecording: boolean;
	isTranscribing: boolean;
	startRecording: () => Promise<void>;
	stopRecording: () => void;
	error: string | null;
	clearError: () => void;
	audioLevel: number;
	recordingDuration: number;
}

function getSupportedMimeType(): string {
	if (typeof MediaRecorder === 'undefined') return '';
	if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
	if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
	return '';
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const result = reader.result;
			if (typeof result !== 'string') {
				reject(new Error('Failed to read audio data'));
				return;
			}
			const base64 = result.split(',')[1];
			if (!base64) {
				reject(new Error('Failed to encode audio data'));
				return;
			}
			resolve(base64);
		};
		reader.onerror = () => reject(new Error('Failed to read audio data'));
		reader.readAsDataURL(blob);
	});
}

export function useVoiceInput({ onTranscription, language }: UseVoiceInputOptions): UseVoiceInputReturn {
	const [isRecording, setIsRecording] = useState(false);
	const [isTranscribing, setIsTranscribing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [audioLevel, setAudioLevel] = useState(0);
	const [recordingDuration, setRecordingDuration] = useState(0);

	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const streamRef = useRef<MediaStream | null>(null);
	const onTranscriptionRef = useRef(onTranscription);
	const languageRef = useRef(language);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const audioContextRef = useRef<AudioContext | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const isStartingRef = useRef(false);

	onTranscriptionRef.current = onTranscription;
	languageRef.current = language;

	const clearError = useCallback(() => setError(null), []);

	const stopAudioAnalysis = useCallback(() => {
		if (animationFrameRef.current !== null) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		if (audioContextRef.current) {
			audioContextRef.current.close();
			audioContextRef.current = null;
		}
		analyserRef.current = null;
		setAudioLevel(0);
	}, []);

	const stopDurationTimer = useCallback(() => {
		if (durationIntervalRef.current !== null) {
			clearInterval(durationIntervalRef.current);
			durationIntervalRef.current = null;
		}
		setRecordingDuration(0);
	}, []);

	const stopMediaTracks = useCallback(() => {
		if (streamRef.current) {
			for (const track of streamRef.current.getTracks()) {
				track.stop();
			}
			streamRef.current = null;
		}
	}, []);

	const startAudioAnalysis = useCallback((stream: MediaStream) => {
		try {
			const audioContext = new AudioContext();
			const source = audioContext.createMediaStreamSource(stream);
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 256;
			analyser.smoothingTimeConstant = 0.8;
			source.connect(analyser);

			audioContextRef.current = audioContext;
			analyserRef.current = analyser;

			const dataArray = new Uint8Array(analyser.frequencyBinCount);

			const updateLevel = () => {
				if (!analyserRef.current) return;
				analyserRef.current.getByteFrequencyData(dataArray);
				let sum = 0;
				for (let i = 0; i < dataArray.length; i++) {
					sum += dataArray[i];
				}
				const average = sum / dataArray.length;
				setAudioLevel(Math.min(average / 128, 1));
				animationFrameRef.current = requestAnimationFrame(updateLevel);
			};

			updateLevel();
		} catch {
			// Web Audio API not available -- degrade gracefully
		}
	}, []);

	const processRecording = useCallback(async (blob: Blob) => {
		setIsTranscribing(true);
		setError(null);
		try {
			const base64 = await blobToBase64(blob);
			const result = await apiClient.transcribeVoice(base64, languageRef.current);
			const text = result.text.trim();
			if (text) {
				onTranscriptionRef.current(text);
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Transcription failed';
			setError(message);
		} finally {
			setIsTranscribing(false);
		}
	}, []);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			mediaRecorderRef.current.stop();
		}
		stopAudioAnalysis();
		stopDurationTimer();
		setIsRecording(false);
	}, [stopAudioAnalysis, stopDurationTimer]);

	const startRecording = useCallback(async () => {
		if (isRecording || isStartingRef.current || mediaRecorderRef.current) return;
		isStartingRef.current = true;
		setError(null);

		if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
			isStartingRef.current = false;
			setError('Microphone access is not available in this context');
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;

			startAudioAnalysis(stream);

			const mimeType = getSupportedMimeType();
			const recorder = mimeType
				? new MediaRecorder(stream, { mimeType })
				: new MediaRecorder(stream);

			chunksRef.current = [];

			recorder.ondataavailable = (e: BlobEvent) => {
				if (e.data.size > 0) {
					chunksRef.current.push(e.data);
				}
			};

			recorder.onstop = () => {
				mediaRecorderRef.current = null;
				stopMediaTracks();
				const chunks = chunksRef.current;
				if (chunks.length > 0) {
					const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
					void processRecording(blob);
				}
				chunksRef.current = [];
			};

			recorder.onerror = () => {
				mediaRecorderRef.current = null;
				setError('Recording failed');
				setIsRecording(false);
				stopAudioAnalysis();
				stopDurationTimer();
				stopMediaTracks();
			};

			mediaRecorderRef.current = recorder;
			recorder.start();
			isStartingRef.current = false;
			setIsRecording(true);
			setRecordingDuration(0);
			durationIntervalRef.current = setInterval(() => {
				setRecordingDuration((prev) => prev + 1);
			}, 1000);
		} catch (err) {
			isStartingRef.current = false;
			stopMediaTracks();
			stopAudioAnalysis();
			if (err instanceof DOMException) {
				if (err.name === 'NotAllowedError') {
					setError('Microphone permission denied');
					return;
				}
				if (err.name === 'NotFoundError') {
					setError('No microphone found');
					return;
				}
			}
			setError('Failed to start recording');
		}
	}, [isRecording, stopMediaTracks, processRecording, startAudioAnalysis, stopAudioAnalysis, stopDurationTimer]);

	useEffect(() => {
		return () => {
			if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
				mediaRecorderRef.current.stop();
			}
			stopMediaTracks();
			stopAudioAnalysis();
			stopDurationTimer();
		};
	}, [stopMediaTracks, stopAudioAnalysis, stopDurationTimer]);

	return {
		isRecording,
		isTranscribing,
		startRecording,
		stopRecording,
		error,
		clearError,
		audioLevel,
		recordingDuration,
	};
}
